import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserEngagementHeatmap = ({ data, onCellClick }) => {
  const [selectedMetric, setSelectedMetric] = useState('usage');
  
  const metrics = [
    { key: 'usage', label: 'Feature Usage', color: 'blue' },
    { key: 'sessions', label: 'Session Count', color: 'green' },
    { key: 'duration', label: 'Session Duration', color: 'purple' }
  ];

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getIntensityColor = (value, max) => {
    const intensity = value / max;
    const colors = {
      blue: {
        low: 'bg-blue-100 border-blue-200',
        medium: 'bg-blue-300 border-blue-400',
        high: 'bg-blue-500 border-blue-600',
        highest: 'bg-blue-700 border-blue-800'
      },
      green: {
        low: 'bg-green-100 border-green-200',
        medium: 'bg-green-300 border-green-400',
        high: 'bg-green-500 border-green-600',
        highest: 'bg-green-700 border-green-800'
      },
      purple: {
        low: 'bg-purple-100 border-purple-200',
        medium: 'bg-purple-300 border-purple-400',
        high: 'bg-purple-500 border-purple-600',
        highest: 'bg-purple-700 border-purple-800'
      }
    };

    const selectedColors = colors[metrics.find(m => m.key === selectedMetric)?.color || 'blue'];
    
    if (intensity === 0) return 'bg-slate-100 border-slate-200';
    if (intensity <= 0.25) return selectedColors.low;
    if (intensity <= 0.5) return selectedColors.medium;
    if (intensity <= 0.75) return selectedColors.high;
    return selectedColors.highest;
  };

  const maxValue = Math.max(...data.flat().map(cell => cell[selectedMetric] || 0));

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">User Engagement Heatmap</h3>
          <p className="text-sm text-text-secondary">Activity patterns by time and day</p>
        </div>
        
        <div className="flex items-center space-x-2">
          {metrics.map((metric) => (
            <Button
              key={metric.key}
              variant={selectedMetric === metric.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMetric(metric.key)}
              className="text-xs"
            >
              {metric.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="space-y-4">
        {/* Hour Labels */}
        <div className="flex items-center">
          <div className="w-12"></div>
          <div className="flex-1 grid grid-cols-24 gap-1">
            {hours.map((hour) => (
              <div key={hour} className="text-xs text-text-secondary text-center">
                {hour % 6 === 0 ? `${hour}h` : ''}
              </div>
            ))}
          </div>
        </div>

        {/* Heatmap Rows */}
        {days.map((day, dayIndex) => (
          <div key={day} className="flex items-center">
            <div className="w-12 text-sm font-medium text-text-secondary">
              {day}
            </div>
            <div className="flex-1 grid grid-cols-24 gap-1">
              {hours.map((hour) => {
                const cellData = data[dayIndex]?.[hour] || { usage: 0, sessions: 0, duration: 0 };
                const value = cellData[selectedMetric] || 0;
                
                return (
                  <button
                    key={`${day}-${hour}`}
                    onClick={() => onCellClick?.(dayIndex, hour, cellData)}
                    className={`
                      aspect-square rounded border transition-all duration-150 hover:scale-110 hover:shadow-elevation-1
                      ${getIntensityColor(value, maxValue)}
                    `}
                    title={`${day} ${hour}:00 - ${metrics.find(m => m.key === selectedMetric)?.label}: ${value}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-text-secondary">Less</span>
          <div className="flex items-center space-x-1">
            {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
              <div
                key={intensity}
                className={`w-4 h-4 rounded border ${getIntensityColor(intensity * maxValue, maxValue)}`}
              />
            ))}
          </div>
          <span className="text-sm text-text-secondary">More</span>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Info" size={14} />
          <span>Click cells for detailed logs</span>
        </div>
      </div>
    </div>
  );
};

export default UserEngagementHeatmap;